export type SitePageType = 'BUSINESS' | 'PRODUCTS';

export type BlockType =
  | 'HERO'
  | 'ABOUT'
  | 'BENEFITS'
  | 'STORY'
  | 'IMAGE_TEXT'
  | 'HIGHLIGHTS'
  | 'FAQ'
  | 'TESTIMONIALS'
  | 'FORM'
  | 'CTA'
  | 'TEXT'
  | 'IMAGE'
  | 'IMAGE_GALLERY'
  | 'BEFORE_AFTER'
  | 'VIDEO'
  | 'STEPS'
  | 'SOCIAL_PROOF'
  | 'WHATSAPP_BUTTON'
  | 'CUSTOM_HTML'
  | 'TIMELINE'
  | 'PROBLEM_SOLUTION'
  | 'TEAM'
  | 'PRODUCT_SHOWCASE'
  | 'ADVANCED_VIDEO';

export interface BackgroundSettings {
  type: 'none' | 'color' | 'gradient' | 'image';
  color: string | null;
  gradient: string | null;
  imageUrl: string | null;
}

export const BACKGROUND_COLORS = [
  '#FDF7F2',
  '#FDE2D6',
  '#F8E7EC',
  '#E7EAF2',
  '#E6F4F1',
  '#F5F5F5',
  '#FFFFFF',
];

export const BACKGROUND_GRADIENTS = [
  'linear-gradient(135deg, #FDE2D6 0%, #F8E7EC 100%)',
  'linear-gradient(135deg, #F8E7EC 0%, #E7EAF2 100%)',
  'linear-gradient(135deg, #E6F4F1 0%, #FDF7F2 100%)',
  'linear-gradient(135deg, #FDE2D6 0%, #FDF7F2 100%)',
];

export interface SitePage {
  id: string;
  user_id: string;
  type: SitePageType;
  slug: string;
  title: string;
  description: string;
  theme_primary_color: string;
  theme_accent_color: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export interface SiteBlock {
  id: string;
  site_page_id: string;
  type: BlockType;
  block_order: number;
  visible: boolean;
  show_in_menu: boolean;
  menu_label: string | null;
  settings: BlockSettings;
  created_at: string;
  updated_at: string;
}

export interface SitePageWithBlocks extends SitePage {
  blocks: SiteBlock[];
}

export interface HeroSettings {
  title: string;
  subtitle: string;
  imageUrl: string;
  buttonLabel: string;
  buttonScrollToBlock?: string;
  layout: 'image-left' | 'image-right' | 'center';
}

export interface AboutSettings {
  title: string;
  text: string;
  imageUrl?: string;
}

export interface BenefitItem {
  icon: string;
  title: string;
  text: string;
}

export interface BenefitsSettings {
  title: string;
  items: BenefitItem[];
}

export interface StorySettings {
  title: string;
  text: string;
  imageUrl?: string;
}

export interface ImageTextSettings {
  title: string;
  text: string;
  imageUrl: string;
  layout: 'image-left' | 'image-right';
}

export interface HighlightItem {
  icon: string;
  title: string;
  text: string;
}

export interface HighlightsSettings {
  title: string;
  items: HighlightItem[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSettings {
  title: string;
  items: FAQItem[];
}

export interface TestimonialItem {
  name: string;
  text: string;
  imageUrl?: string;
}

export interface TestimonialsSettings {
  title: string;
  testimonials: TestimonialItem[];
}

export interface FormBlockSettings {
  title: string;
  formId: string | null;
}

export interface CTASettings {
  title: string;
  buttonLabel: string;
  buttonScrollToBlock?: string;
}

export interface TextSettings {
  title: string;
  text: string;
}

export interface ImageSettings {
  imageUrl: string;
  caption?: string;
  alt?: string;
}

export interface ImageGallerySettings {
  images: { url: string; caption?: string }[];
}

export interface BeforeAfterSettings {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
}

export interface VideoSettings {
  videoUrl: string;
  title?: string;
}

export interface StepItem {
  number: number;
  title: string;
  text: string;
}

export interface StepsSettings {
  title: string;
  steps: StepItem[];
}

export interface SocialProofSettings {
  title: string;
  items: { label: string; value: string }[];
}

export interface WhatsAppSettings {
  phoneNumber: string;
  message: string;
  buttonLabel: string;
}

export interface CustomHTMLSettings {
  html: string;
}

export interface TimelineItem {
  yearOrStep: string;
  title: string;
  description: string;
  icon: string | null;
  imageUrl: string | null;
}

export interface TimelineSettings {
  title: string;
  subtitle: string;
  items: TimelineItem[];
  background?: BackgroundSettings;
}

export interface ProblemSolutionItem {
  text: string;
  icon: string | null;
}

export interface ProblemSolutionSettings {
  title: string;
  problems: ProblemSolutionItem[];
  solutions: ProblemSolutionItem[];
  layout: 'left-right' | 'stacked';
  background?: BackgroundSettings;
}

export interface TeamMember {
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
}

export interface TeamSettings {
  title: string;
  subtitle: string;
  members: TeamMember[];
  columns: 2 | 3 | 4;
  background?: BackgroundSettings;
}

// Legacy ProductItem - kept for backward compatibility with existing blocks
export interface ProductItem {
  imageUrl: string;
  name: string;
  description: string;
  badge: string | null;
}

// New SelectedProduct reference - links to products table with optional overrides
export interface SelectedProductRef {
  productId: string;
  customName?: string;        // Override the default product name
  customDescription?: string; // Override the default tagline
  customBadge?: string;       // Custom badge like "Bestseller", "Neu", etc.
}

export interface ProductShowcaseSettings {
  title: string;
  subtitle: string;
  // New: Selected products from database
  selectedProducts?: SelectedProductRef[];
  // Legacy: Manual product entries (for backward compatibility)
  products?: ProductItem[];
  columns: 2 | 3;
  background?: BackgroundSettings;
}

export interface AdvancedVideoSettings {
  title: string;
  videoType: 'youtube' | 'upload';
  youtubeUrl: string | null;
  videoUrl: string | null;
  autoplay: boolean;
  rounded: boolean;
  background?: BackgroundSettings;
}

export type BlockSettings =
  | HeroSettings
  | AboutSettings
  | BenefitsSettings
  | StorySettings
  | ImageTextSettings
  | HighlightsSettings
  | FAQSettings
  | TestimonialsSettings
  | FormBlockSettings
  | CTASettings
  | TextSettings
  | ImageSettings
  | ImageGallerySettings
  | BeforeAfterSettings
  | VideoSettings
  | StepsSettings
  | SocialProofSettings
  | WhatsAppSettings
  | CustomHTMLSettings
  | TimelineSettings
  | ProblemSolutionSettings
  | TeamSettings
  | ProductShowcaseSettings
  | AdvancedVideoSettings
  | Record<string, unknown>;

export interface UpdateSitePageInput {
  slug?: string;
  title?: string;
  description?: string;
  theme_primary_color?: string;
  theme_accent_color?: string;
  is_published?: boolean;
}

export interface CreateBlockInput {
  site_page_id: string;
  type: BlockType;
  block_order: number;
  visible?: boolean;
  show_in_menu?: boolean;
  menu_label?: string | null;
  settings: BlockSettings;
}

export interface UpdateBlockInput {
  block_order?: number;
  visible?: boolean;
  show_in_menu?: boolean;
  menu_label?: string | null;
  settings?: BlockSettings;
}

export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  HERO: 'Hero-Bereich',
  ABOUT: 'Über mich',
  BENEFITS: 'Vorteile',
  STORY: 'Meine Geschichte',
  IMAGE_TEXT: 'Bild & Text',
  HIGHLIGHTS: 'Highlights',
  FAQ: 'FAQ',
  TESTIMONIALS: 'Erfahrungen',
  FORM: 'Formular',
  CTA: 'Call-to-Action',
  TEXT: 'Text',
  IMAGE: 'Bild',
  IMAGE_GALLERY: 'Bildergalerie',
  BEFORE_AFTER: 'Vorher/Nachher',
  VIDEO: 'Video',
  STEPS: 'Schritte',
  SOCIAL_PROOF: 'Social Proof',
  WHATSAPP_BUTTON: 'WhatsApp Button',
  CUSTOM_HTML: 'Custom HTML',
  TIMELINE: 'Timeline',
  PROBLEM_SOLUTION: 'Problem & Lösung',
  TEAM: 'Team',
  PRODUCT_SHOWCASE: 'Produkt-Showcase',
  ADVANCED_VIDEO: 'Erweitertes Video',
};

export const PAGE_TYPE_LABELS: Record<SitePageType, string> = {
  BUSINESS: 'Business-Seite',
  PRODUCTS: 'Produkte-Seite',
};
